import { useTableStore } from '@/stores/tableStore'
import type { RestaurantTable } from '@/types'

interface QRCodeDisplayProps {
  table: RestaurantTable
  onClose: () => void
}

export default function QRCodeDisplay({ table, onClose }: QRCodeDisplayProps) {
  const { regenerateQRCode } = useTableStore()

  const handleDownload = () => {
    if (!table.qrCodeData) {
      return
    }

    // Convert data URL to blob and download
    const link = document.createElement('a')
    link.href = table.qrCodeData
    link.download = `qr-table-${table.tableNumber}.png`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handlePrint = () => {
    if (!table.qrCodeData) {
      return
    }

    // Open print dialog with QR code
    const printWindow = window.open('', '_blank')
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>QR Code - Tavolo ${table.tableNumber}</title>
            <style>
              @page {
                size: A4;
                margin: 2cm;
              }
              body {
                font-family: Arial, sans-serif;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                min-height: 100vh;
                margin: 0;
              }
              .container {
                text-align: center;
              }
              h1 {
                font-size: 48px;
                margin: 0 0 20px 0;
                color: #1a1a1a;
              }
              .table-info {
                font-size: 24px;
                color: #666;
                margin-bottom: 40px;
              }
              img {
                width: 400px;
                height: 400px;
                border: 2px solid #e5e7eb;
                border-radius: 8px;
              }
              .instructions {
                margin-top: 40px;
                font-size: 18px;
                color: #666;
                max-width: 500px;
              }
              @media print {
                body {
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                }
              }
            </style>
          </head>
          <body>
            <div class="container">
              <h1>Tavolo ${table.tableNumber}</h1>
              ${table.location ? `<div class="table-info">${table.location}</div>` : ''}
              <img src="${table.qrCodeData}" alt="QR Code Tavolo ${table.tableNumber}" />
              <div class="instructions">
                Scansiona questo codice QR con la fotocamera<br/>
                del tuo smartphone per ordinare
              </div>
            </div>
          </body>
        </html>
      `)
      printWindow.document.close()

      // Wait for image to load before printing
      setTimeout(() => {
        printWindow.print()
        printWindow.close()
      }, 500)
    }
  }

  const handleRegenerate = async () => {
    if (window.confirm('Sei sicuro di voler rigenerare il codice QR? Il vecchio QR non funzionerà più.')) {
      try {
        await regenerateQRCode(table.id)
      } catch (error) {
        // Error handled by store
      }
    }
  }

  const handleCopyLink = () => {
    if (table.qrCodeUrl) {
      navigator.clipboard.writeText(table.qrCodeUrl)
        .then(() => {
          alert('Link copiato negli appunti!')
        })
        .catch(() => {
          alert('Errore nella copia del link')
        })
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#25252d] rounded-2xl shadow-xl max-w-2xl w-full p-6 border border-gray-200 dark:border-white/[0.08]">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Codice QR - Tavolo {table.tableNumber}
            </h2>
            {table.location && (
              <p className="text-sm text-gray-600 dark:text-white/60 mt-1">{table.location}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 dark:text-white/40 hover:text-gray-600 dark:hover:text-white/70 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {/* QR Code Display */}
        <div className="flex flex-col items-center mb-6">
          {table.qrCodeData ? (
            <div className="relative">
              <div className="p-4 bg-white rounded-xl shadow-md">
                <img
                  src={table.qrCodeData}
                  alt={`QR Code Tavolo ${table.tableNumber}`}
                  className="w-72 h-72"
                />
              </div>
              <div className="mt-4 text-center">
                <p className="text-sm text-gray-600 dark:text-white/60">
                  Scansiona questo codice per accedere al menu
                </p>
              </div>
            </div>
          ) : (
            <div className="w-80 h-80 border-2 border-dashed border-gray-300 dark:border-white/20 rounded-xl flex items-center justify-center bg-gray-50 dark:bg-white/[0.02]">
              <p className="text-gray-400 dark:text-white/40">Nessun QR code disponibile</p>
            </div>
          )}
        </div>

        {/* URL Display */}
        {table.qrCodeUrl && (
          <div className="mb-6 p-4 bg-gray-50 dark:bg-white/[0.04] rounded-xl border border-gray-200 dark:border-white/[0.08]">
            <label className="block text-sm font-medium text-gray-700 dark:text-white/70 mb-2">
              URL del tavolo:
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={table.qrCodeUrl}
                readOnly
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-white/20 rounded-lg bg-white dark:bg-white/[0.06] text-sm text-gray-900 dark:text-white"
              />
              <button
                onClick={handleCopyLink}
                className="px-4 py-2 bg-gray-200 dark:bg-white/[0.1] text-gray-700 dark:text-white/70 rounded-lg hover:bg-gray-300 dark:hover:bg-white/[0.15] transition text-sm font-medium"
              >
                Copia
              </button>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <button
            onClick={handleDownload}
            className="px-4 py-2.5 bg-blue-600 dark:bg-blue-500 text-white rounded-xl hover:bg-blue-700 dark:hover:bg-blue-600 transition font-medium text-sm"
          >
            Download PNG
          </button>
          <button
            onClick={handlePrint}
            className="px-4 py-2.5 bg-emerald-600 dark:bg-emerald-500 text-white rounded-xl hover:bg-emerald-700 dark:hover:bg-emerald-600 transition font-medium text-sm"
          >
            Stampa
          </button>
          <button
            onClick={handleRegenerate}
            className="px-4 py-2.5 bg-amber-500 dark:bg-amber-500 text-white rounded-xl hover:bg-amber-600 dark:hover:bg-amber-600 transition font-medium text-sm"
          >
            Rigenera
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2.5 bg-gray-500 dark:bg-white/[0.1] text-white dark:text-white/70 rounded-xl hover:bg-gray-600 dark:hover:bg-white/[0.15] transition font-medium text-sm"
          >
            Chiudi
          </button>
        </div>

        {/* Info Box */}
        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 rounded-xl">
          <h3 className="font-semibold text-blue-900 dark:text-blue-400 mb-2">Suggerimenti:</h3>
          <ul className="text-sm text-blue-800 dark:text-blue-300/80 space-y-1">
            <li>• Stampa il QR e posizionalo in modo visibile sul tavolo</li>
            <li>• Plastifica il QR per proteggerlo dall'usura</li>
            <li>• Ogni tavolo ha un QR unico per tracciare gli ordini</li>
            <li>• Rigenera il QR solo se necessario (il vecchio smetterà di funzionare)</li>
          </ul>
        </div>
      </div>
    </div>
  )
}
